import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import {
  deactivateAppraisalBehavior,
  deactivateAppraisalBehaviorGroup,
  deactivateAppraisalValue,
  getAppraisalBehaviorGroups,
  getAppraisalBehaviors,
  getAppraisalValues,
  saveAppraisalBehaviorGroup,
  saveAppraisalBehavior,
  saveAppraisalValue,
} from "./appraisalApi";
import { APPRAISAL_LIMITS, clampLen, validateFields } from "./appraisalValidation";
import { AppraisalCharCount } from "./AppraisalFormFields";
import "./Appraisal.css";

const FRAMEWORK_HELP = {
  values: {
    icon: "bi-gem",
    titleKey: "app.appraisal.framework.help.valuesTitle",
    titleDefault: "What are Values?",
    meaningKey: "app.appraisal.framework.help.valuesMeaning",
    meaningDefault:
      "Values are your company’s core principles — for an engineering org: Craftsmanship, Collaboration, Ownership.",
    whyKey: "app.appraisal.framework.help.valuesWhy",
    whyDefault:
      "They keep reviews fair and consistent. Engineers are judged against shared engineering culture, not each manager’s personal opinion.",
    exampleKey: "app.appraisal.framework.help.valuesExample",
    exampleDefault: "Example: Craftsmanship, Collaboration, Customer Impact, Continuous Learning.",
    howKey: "app.appraisal.framework.help.valuesHow",
    howDefault:
      "Create Values first. They organise Behaviors, but Values themselves are not scored directly.",
  },
  groups: {
    icon: "bi-collection",
    titleKey: "app.appraisal.framework.help.groupsTitle",
    titleDefault: "What are Behavior Groups?",
    meaningKey: "app.appraisal.framework.help.groupsMeaning",
    meaningDefault:
      "A Behavior Group is a package of behaviors for a role — e.g. Software Engineering, QA, Platform / DevOps.",
    whyKey: "app.appraisal.framework.help.groupsWhy",
    whyDefault:
      "Backend, frontend, and QA need different checklists without rewriting the whole framework.",
    exampleKey: "app.appraisal.framework.help.groupsExample",
    exampleDefault: "Example: Software Engineering, QA Engineering, Engineering Managers.",
    howKey: "app.appraisal.framework.help.groupsHow",
    howDefault:
      "Create groups inside Values & Behaviors. When you add a Behavior, choose which group it belongs to.",
  },
  behaviors: {
    icon: "bi-list-check",
    titleKey: "app.appraisal.framework.help.behaviorsTitle",
    titleDefault: "What are Behaviors?",
    meaningKey: "app.appraisal.framework.help.behaviorsMeaning",
    meaningDefault:
      "Behaviors are the clear, observable statements people actually rate during an appraisal.",
    whyKey: "app.appraisal.framework.help.behaviorsWhy",
    whyDefault:
      "They turn engineering culture into measurable performance — clearer feedback, fairer scores, and better pay decisions.",
    exampleKey: "app.appraisal.framework.help.behaviorsExample",
    exampleDefault:
      "Example under Craftsmanship: “Writes clear PRs with tests and risk notes” or “Leaves code easier to maintain than they found it.”",
    howKey: "app.appraisal.framework.help.behaviorsHow",
    howDefault:
      "Each Behavior is linked to one Value and one Behavior Group. Staff, managers, and meetings score these statements.",
  },
  overview: {
    icon: "bi-diagram-3",
    titleKey: "app.appraisal.framework.help.overviewTitle",
    titleDefault: "How Values & Behaviors fit together",
    meaningKey: "app.appraisal.framework.help.overviewMeaning",
    meaningDefault:
      "Values are the principles. Behavior Groups package expectations by role. Behaviors are the rated statements under a Value + Group link.",
    whyKey: "app.appraisal.framework.help.overviewWhy",
    whyDefault:
      "This structure makes appraisals consistent across engineers and useful for coaching, compliance, and pay recommendations.",
    exampleKey: "app.appraisal.framework.help.overviewExample",
    exampleDefault:
      "Example: Value “Craftsmanship” → Group “Software Engineering” → Behavior “Writes clear PRs with tests.”",
    howKey: "app.appraisal.framework.help.overviewHow",
    howDefault:
      "Setup order: 1) Values, 2) Behavior Groups, 3) Behaviors, 4) Objectives, 5) Retrospectives. Then start a cycle.",
  },
};

const FrameworkHelpModal = ({ helpKey, onClose, t }) => {
  if (!helpKey) return null;
  const help = FRAMEWORK_HELP[helpKey];
  if (!help) return null;

  return (
    <div
      className="appraisal-help-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="appraisal-help-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="appraisal-framework-help-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="appraisal-help-modal__header">
          <span className="appraisal-help-modal__icon" aria-hidden>
            <i className={`bi ${help.icon}`} />
          </span>
          <div>
            <h2 id="appraisal-framework-help-title">
              {t(help.titleKey, { defaultValue: help.titleDefault })}
            </h2>
            <p className="appraisal-help-modal__subtitle">
              {t("app.appraisal.framework.help.popupSubtitle", {
                defaultValue: "Quick guide so your team sets this up correctly.",
              })}
            </p>
          </div>
          <button
            type="button"
            className="appraisal-help-modal__close"
            onClick={onClose}
            aria-label={t("app.appraisal.framework.help.close", { defaultValue: "Close" })}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="appraisal-help-modal__body">
          <div className="appraisal-help-block">
            <h3>{t("app.appraisal.framework.help.meaning", { defaultValue: "Meaning" })}</h3>
            <p>{t(help.meaningKey, { defaultValue: help.meaningDefault })}</p>
          </div>
          <div className="appraisal-help-block">
            <h3>{t("app.appraisal.framework.help.whyUseful", { defaultValue: "Why it is useful" })}</h3>
            <p>{t(help.whyKey, { defaultValue: help.whyDefault })}</p>
          </div>
          <div className="appraisal-help-block">
            <h3>{t("app.appraisal.framework.help.example", { defaultValue: "Example" })}</h3>
            <p>{t(help.exampleKey, { defaultValue: help.exampleDefault })}</p>
          </div>
          <div className="appraisal-help-block">
            <h3>{t("app.appraisal.framework.help.howItWorks", { defaultValue: "How it works here" })}</h3>
            <p>{t(help.howKey, { defaultValue: help.howDefault })}</p>
          </div>

          {helpKey === "overview" ? (
            <ol className="appraisal-help-steps">
              <li>
                <i className="bi bi-gem" aria-hidden />
                <span>{t("app.appraisal.framework.help.stepValues", { defaultValue: "Create Values (company principles)." })}</span>
              </li>
              <li>
                <i className="bi bi-collection" aria-hidden />
                <span>{t("app.appraisal.framework.help.stepGroups", { defaultValue: "Create Behavior Groups (role packages)." })}</span>
              </li>
              <li>
                <i className="bi bi-list-check" aria-hidden />
                <span>{t("app.appraisal.framework.help.stepBehaviors", { defaultValue: "Add Behaviors linked to a Value + Group." })}</span>
              </li>
            </ol>
          ) : null}
        </div>

        <div className="appraisal-help-modal__footer">
          <button type="button" className="appraisal-btn primary" onClick={onClose}>
            {t("app.appraisal.framework.help.gotIt", { defaultValue: "Got it" })}
          </button>
        </div>
      </div>
    </div>
  );
};

const FrameworkSectionHeader = ({ icon, title, helpKey, onOpenHelp, t }) => (
  <div className="appraisal-framework-section-head">
    <div className="appraisal-framework-section-head__title">
      <span className={`appraisal-framework-section-icon appraisal-framework-section-icon--${helpKey}`} aria-hidden>
        <i className={`bi ${icon}`} />
      </span>
      <h3>{title}</h3>
    </div>
    <button
      type="button"
      className="appraisal-framework-info-btn"
      onClick={() => onOpenHelp(helpKey)}
      aria-label={t("app.appraisal.framework.help.openAria", {
        defaultValue: "Learn more about {{title}}",
        title,
      })}
      title={t("app.appraisal.framework.help.openTitle", { defaultValue: "What is this?" })}
    >
      <i className="bi bi-info-circle" aria-hidden />
      <span>{t("app.appraisal.framework.help.learn", { defaultValue: "What is this?" })}</span>
    </button>
  </div>
);

const AppraisalFrameworkPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [values, setValues] = useState([]);
  const [groups, setGroups] = useState([]);
  const [behaviors, setBehaviors] = useState([]);
  const [helpKey, setHelpKey] = useState(null);
  const [valueForm, setValueForm] = useState({ name: "", description: "" });
  const [groupForm, setGroupForm] = useState({ name: "", description: "" });
  const [behaviorForm, setBehaviorForm] = useState({
    appraisalValueId: "",
    appraisalBehaviorGroupId: "",
    introduction: "",
    statement: "",
    helpText: "",
    sortOrder: 0,
  });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  /** @type {[null | 'value' | 'group' | 'behavior', Function]} */
  const [saving, setSaving] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const [v, g, b] = await Promise.all([
        getAppraisalValues(),
        getAppraisalBehaviorGroups(),
        getAppraisalBehaviors(),
      ]);
      setValues(v || []);
      setGroups(g || []);
      setBehaviors(b || []);
      setBehaviorForm((prev) => ({
        ...prev,
        appraisalValueId: prev.appraisalValueId || v?.[0]?.id || "",
        appraisalBehaviorGroupId: prev.appraisalBehaviorGroupId || g?.[0]?.id || "",
      }));
    } catch (e) {
      setError(e?.message || "Failed to load framework");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!helpKey) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setHelpKey(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [helpKey]);

  const onSaveValue = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const err = validateFields([
      { value: valueForm.name, required: true, max: APPRAISAL_LIMITS.name, label: "Name" },
      { value: valueForm.description, max: APPRAISAL_LIMITS.description, label: "Description" },
    ]);
    if (err) {
      setError(err);
      return;
    }
    setSaving("value");
    try {
      await saveAppraisalValue({ ...valueForm, isActive: true });
      setValueForm({ name: "", description: "" });
      setMessage(t("app.appraisal.framework.valueSaved", { defaultValue: "Value saved." }));
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to save value");
    } finally {
      setSaving(null);
    }
  };

  const onSaveGroup = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const err = validateFields([
      { value: groupForm.name, required: true, max: APPRAISAL_LIMITS.name, label: "Name" },
      { value: groupForm.description, max: APPRAISAL_LIMITS.description, label: "Description" },
    ]);
    if (err) {
      setError(err);
      return;
    }
    setSaving("group");
    try {
      await saveAppraisalBehaviorGroup({ ...groupForm, isActive: true });
      setGroupForm({ name: "", description: "" });
      setMessage(t("app.appraisal.framework.groupSaved", { defaultValue: "Behavior group saved." }));
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to save group");
    } finally {
      setSaving(null);
    }
  };

  const onSaveBehavior = async (e) => {
    e.preventDefault();
    if (saving) return;
    setMessage("");
    setError("");
    const err = validateFields([
      { value: behaviorForm.introduction, max: APPRAISAL_LIMITS.behaviorText, label: "Introduction" },
      { value: behaviorForm.statement, required: true, max: APPRAISAL_LIMITS.behaviorText, label: "Statement" },
      { value: behaviorForm.helpText, max: APPRAISAL_LIMITS.behaviorText, label: "Help text" },
    ]);
    if (err) {
      setError(err);
      return;
    }
    setSaving("behavior");
    try {
      await saveAppraisalBehavior({ ...behaviorForm, isActive: true });
      setBehaviorForm((prev) => ({ ...prev, introduction: "", statement: "", helpText: "", sortOrder: 0 }));
      setMessage(t("app.appraisal.framework.behaviorSaved", { defaultValue: "Behavior saved." }));
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to save behavior");
    } finally {
      setSaving(null);
    }
  };

  const openDeleteConfirm = (type, item) => {
    const labels = {
      value: {
        title: t("app.appraisal.framework.deleteValueTitle", { defaultValue: "Delete value" }),
        lead: t("app.appraisal.framework.deleteValueLead", {
          defaultValue:
            "This removes the value from the framework. Linked behaviors under this value will also be removed from active lists.",
        }),
        name: item.name,
      },
      group: {
        title: t("app.appraisal.framework.deleteGroupTitle", { defaultValue: "Delete behavior group" }),
        lead: t("app.appraisal.framework.deleteGroupLead", {
          defaultValue:
            "This removes the group from the framework. Linked behaviors under this group will also be removed from active lists.",
        }),
        name: item.name,
      },
      behavior: {
        title: t("app.appraisal.framework.deleteBehaviorTitle", { defaultValue: "Delete behavior" }),
        lead: t("app.appraisal.framework.deleteBehaviorLead", {
          defaultValue: "This removes the behavior statement from active appraisals going forward.",
        }),
        name: item.statement,
      },
    };
    setDeleteConfirm({ type, id: item.id, ...labels[type] });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm || deleteLoading) return;
    setDeleteLoading(true);
    setError("");
    setMessage("");
    try {
      if (deleteConfirm.type === "value") await deactivateAppraisalValue(deleteConfirm.id);
      else if (deleteConfirm.type === "group") await deactivateAppraisalBehaviorGroup(deleteConfirm.id);
      else await deactivateAppraisalBehavior(deleteConfirm.id);
      setDeleteConfirm(null);
      setMessage(t("app.appraisal.framework.deleted", { defaultValue: "Removed from framework." }));
      await load({ silent: true });
    } catch (err) {
      setError(err?.message || "Failed to delete");
      setDeleteConfirm(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const canConfigureBehaviors = values.length > 0 && groups.length > 0;

  return (
    <div className="appraisal-page appraisal-framework-page">
      <CommonPageHeader
        icon={<i className="bi bi-diagram-3" />}
        title={t("app.appraisal.frameworkTitle", { defaultValue: "Values & Behaviors" })}
        description={t("app.appraisal.frameworkSubtitle", {
          defaultValue: "Define organisational values, groups, and behavioral statements.",
        })}
        rightContent={
          <button
            type="button"
            className="appraisal-framework-guide-btn"
            onClick={() => setHelpKey("overview")}
          >
            <i className="bi bi-info-circle" aria-hidden />
            {t("app.appraisal.framework.help.guideBtn", { defaultValue: "How this works" })}
          </button>
        }
      />
      {loading ? <div className="appraisal-muted">Loading…</div> : null}
      {error ? <div className="appraisal-error">{error}</div> : null}
      {message ? <div className="appraisal-success">{message}</div> : null}

      {!loading && (
        <div className="appraisal-framework-layout">
          <div className="appraisal-framework-setup">
            <section className="appraisal-settings-panel appraisal-framework-panel">
              <div className="appraisal-settings-panel__head">
                <FrameworkSectionHeader
                  icon="bi-gem"
                  title={t("app.appraisal.framework.valuesTitle", { defaultValue: "Values" })}
                  helpKey="values"
                  onOpenHelp={setHelpKey}
                  t={t}
                />
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.framework.valuesHint", {
                    defaultValue: "Company principles like Integrity or Teamwork. These organise what people are scored on.",
                  })}
                </p>
              </div>

              <form onSubmit={onSaveValue} className="appraisal-framework-form">
                <label className="appraisal-field">
                  {t("app.appraisal.framework.valueName", { defaultValue: "Name" })}
                  <input
                    required
                    maxLength={APPRAISAL_LIMITS.name}
                    placeholder={t("app.appraisal.framework.valueNamePlaceholder", {
                      defaultValue: "e.g. Craftsmanship",
                    })}
                    value={valueForm.name}
                    onChange={(e) =>
                      setValueForm((p) => ({ ...p, name: clampLen(e.target.value, APPRAISAL_LIMITS.name) }))
                    }
                  />
                  <AppraisalCharCount value={valueForm.name} max={APPRAISAL_LIMITS.name} />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.framework.valueDesc", { defaultValue: "Description" })}
                  <textarea
                    rows={3}
                    maxLength={APPRAISAL_LIMITS.description}
                    placeholder={t("app.appraisal.framework.valueDescPlaceholder", {
                      defaultValue: "e.g. Care about code quality and maintainability",
                    })}
                    value={valueForm.description}
                    onChange={(e) =>
                      setValueForm((p) => ({
                        ...p,
                        description: clampLen(e.target.value, APPRAISAL_LIMITS.description),
                      }))
                    }
                  />
                  <AppraisalCharCount value={valueForm.description} max={APPRAISAL_LIMITS.description} />
                </label>
                <div className="appraisal-framework-form__actions">
                  <button type="submit" className="appraisal-btn primary" disabled={!!saving}>
                    {saving === "value"
                      ? t("app.common.saving", { defaultValue: "Saving…" })
                      : t("app.appraisal.framework.addValue", { defaultValue: "Add value" })}
                  </button>
                </div>
              </form>

              <div className="appraisal-framework-list-block">
                <div className="appraisal-framework-list-block__head">
                  <span>
                    {t("app.appraisal.framework.valuesList", {
                      defaultValue: "Added values",
                    })}
                  </span>
                  <span className="appraisal-framework-count">{values.length}</span>
                </div>
                {values.length === 0 ? (
                  <div className="appraisal-framework-empty">
                    <i className="bi bi-gem" aria-hidden />
                    <p>
                      {t("app.appraisal.framework.valuesEmpty", {
                        defaultValue: "No values yet. Add your first company principle above.",
                      })}
                    </p>
                  </div>
                ) : (
                  <ul className="appraisal-framework-items">
                    {values.map((v) => (
                      <li key={v.id} className="appraisal-framework-item">
                        <div className="appraisal-framework-item__body">
                          <strong>{v.name}</strong>
                          {v.description ? <span>{v.description}</span> : null}
                        </div>
                        <button
                          type="button"
                          className="appraisal-list-delete"
                          onClick={() => openDeleteConfirm("value", v)}
                          title={t("app.common.delete", { defaultValue: "Delete" })}
                        >
                          <i className="bi bi-trash3" aria-hidden />
                          <span>{t("app.common.delete", { defaultValue: "Delete" })}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>

            <section className="appraisal-settings-panel appraisal-framework-panel">
              <div className="appraisal-settings-panel__head">
                <FrameworkSectionHeader
                  icon="bi-collection"
                  title={t("app.appraisal.framework.groupsTitle", { defaultValue: "Behavior groups" })}
                  helpKey="groups"
                  onOpenHelp={setHelpKey}
                  t={t}
                />
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.framework.groupsHint", {
                    defaultValue: "Role packages such as Software Engineering or QA. Different jobs get different checklists.",
                  })}
                </p>
              </div>

              <form onSubmit={onSaveGroup} className="appraisal-framework-form">
                <label className="appraisal-field">
                  {t("app.appraisal.framework.groupName", { defaultValue: "Name" })}
                  <input
                    required
                    maxLength={APPRAISAL_LIMITS.name}
                    placeholder={t("app.appraisal.framework.groupNamePlaceholder", {
                      defaultValue: "e.g. Software Engineering",
                    })}
                    value={groupForm.name}
                    onChange={(e) =>
                      setGroupForm((p) => ({ ...p, name: clampLen(e.target.value, APPRAISAL_LIMITS.name) }))
                    }
                  />
                  <AppraisalCharCount value={groupForm.name} max={APPRAISAL_LIMITS.name} />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.framework.groupDesc", { defaultValue: "Description" })}
                  <textarea
                    rows={3}
                    maxLength={APPRAISAL_LIMITS.description}
                    placeholder={t("app.appraisal.framework.groupDescPlaceholder", {
                      defaultValue: "e.g. Backend / fullstack engineers",
                    })}
                    value={groupForm.description}
                    onChange={(e) =>
                      setGroupForm((p) => ({
                        ...p,
                        description: clampLen(e.target.value, APPRAISAL_LIMITS.description),
                      }))
                    }
                  />
                  <AppraisalCharCount value={groupForm.description} max={APPRAISAL_LIMITS.description} />
                </label>
                <div className="appraisal-framework-form__actions">
                  <button type="submit" className="appraisal-btn primary" disabled={!!saving}>
                    {saving === "group"
                      ? t("app.common.saving", { defaultValue: "Saving…" })
                      : t("app.appraisal.framework.addGroup", { defaultValue: "Add group" })}
                  </button>
                </div>
              </form>

              <div className="appraisal-framework-list-block">
                <div className="appraisal-framework-list-block__head">
                  <span>
                    {t("app.appraisal.framework.groupsList", {
                      defaultValue: "Added groups",
                    })}
                  </span>
                  <span className="appraisal-framework-count">{groups.length}</span>
                </div>
                {groups.length === 0 ? (
                  <div className="appraisal-framework-empty">
                    <i className="bi bi-collection" aria-hidden />
                    <p>
                      {t("app.appraisal.framework.groupsEmpty", {
                        defaultValue: "No behavior groups yet. Add a role package above.",
                      })}
                    </p>
                  </div>
                ) : (
                  <ul className="appraisal-framework-items">
                    {groups.map((g) => (
                      <li key={g.id} className="appraisal-framework-item">
                        <div className="appraisal-framework-item__body">
                          <strong>{g.name}</strong>
                          {g.description ? <span>{g.description}</span> : null}
                        </div>
                        <button
                          type="button"
                          className="appraisal-list-delete"
                          onClick={() => openDeleteConfirm("group", g)}
                          title={t("app.common.delete", { defaultValue: "Delete" })}
                        >
                          <i className="bi bi-trash3" aria-hidden />
                          <span>{t("app.common.delete", { defaultValue: "Delete" })}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </div>

          {!canConfigureBehaviors ? (
            <section className="appraisal-settings-panel appraisal-framework-locked">
              <div className="appraisal-framework-locked__icon" aria-hidden>
                <i className="bi bi-list-check" />
              </div>
              <div className="appraisal-framework-locked__copy">
                <strong>
                  {t("app.appraisal.framework.behaviorsLockedTitle", {
                    defaultValue: "Behaviors come next",
                  })}
                </strong>
                <p>
                  {t("app.appraisal.framework.behaviorsLockedBody", {
                    defaultValue:
                      "Add at least one Value and one Behavior Group first. Behaviors link to both, so this section unlocks when both are ready.",
                  })}
                </p>
                <ul className="appraisal-framework-checklist">
                  <li className={values.length > 0 ? "is-ready" : ""}>
                    <i className={`bi ${values.length > 0 ? "bi-check-circle-fill" : "bi-circle"}`} aria-hidden />
                    <span>
                      {t("app.appraisal.framework.behaviorsNeedValue", {
                        defaultValue: "At least one Value",
                      })}
                    </span>
                  </li>
                  <li className={groups.length > 0 ? "is-ready" : ""}>
                    <i className={`bi ${groups.length > 0 ? "bi-check-circle-fill" : "bi-circle"}`} aria-hidden />
                    <span>
                      {t("app.appraisal.framework.behaviorsNeedGroup", {
                        defaultValue: "At least one Behavior Group",
                      })}
                    </span>
                  </li>
                </ul>
              </div>
            </section>
          ) : (
            <section className="appraisal-settings-panel appraisal-framework-panel appraisal-framework-panel--wide">
              <div className="appraisal-settings-panel__head">
                <FrameworkSectionHeader
                  icon="bi-list-check"
                  title={t("app.appraisal.framework.behaviorsTitle", { defaultValue: "Behaviors" })}
                  helpKey="behaviors"
                  onOpenHelp={setHelpKey}
                  t={t}
                />
                <p className="appraisal-framework-hint">
                  {t("app.appraisal.framework.behaviorsHint", {
                    defaultValue: "Observable statements people rate. Link each one to a Value and a Behavior Group.",
                  })}
                </p>
              </div>

              <form onSubmit={onSaveBehavior} className="appraisal-framework-form">
                <div className="appraisal-grid-2">
                  <label className="appraisal-field">
                    {t("app.appraisal.framework.selectValue", { defaultValue: "Select value" })}
                    <select
                      required
                      value={behaviorForm.appraisalValueId}
                      onChange={(e) => setBehaviorForm((p) => ({ ...p, appraisalValueId: e.target.value }))}
                    >
                      <option value="">
                        {t("app.appraisal.framework.selectValue", { defaultValue: "Select value" })}
                      </option>
                      {values.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </label>
                  <label className="appraisal-field">
                    {t("app.appraisal.framework.selectGroup", { defaultValue: "Select group" })}
                    <select
                      required
                      value={behaviorForm.appraisalBehaviorGroupId}
                      onChange={(e) => setBehaviorForm((p) => ({ ...p, appraisalBehaviorGroupId: e.target.value }))}
                    >
                      <option value="">
                        {t("app.appraisal.framework.selectGroup", { defaultValue: "Select group" })}
                      </option>
                      {groups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <label className="appraisal-field">
                  {t("app.appraisal.framework.introduction", { defaultValue: "Introduction" })}
                  <input
                    maxLength={APPRAISAL_LIMITS.behaviorText}
                    placeholder={t("app.appraisal.framework.introductionPlaceholder", {
                      defaultValue: "Optional short intro",
                    })}
                    value={behaviorForm.introduction}
                    onChange={(e) =>
                      setBehaviorForm((p) => ({
                        ...p,
                        introduction: clampLen(e.target.value, APPRAISAL_LIMITS.behaviorText),
                      }))
                    }
                  />
                  <AppraisalCharCount value={behaviorForm.introduction} max={APPRAISAL_LIMITS.behaviorText} />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.framework.statement", { defaultValue: "Statement" })}
                  <textarea
                    required
                    rows={3}
                    maxLength={APPRAISAL_LIMITS.behaviorText}
                    placeholder={t("app.appraisal.framework.statementPlaceholder", {
                      defaultValue: "e.g. Writes clear PRs with tests and risk notes",
                    })}
                    value={behaviorForm.statement}
                    onChange={(e) =>
                      setBehaviorForm((p) => ({
                        ...p,
                        statement: clampLen(e.target.value, APPRAISAL_LIMITS.behaviorText),
                      }))
                    }
                  />
                  <AppraisalCharCount value={behaviorForm.statement} max={APPRAISAL_LIMITS.behaviorText} />
                </label>
                <label className="appraisal-field">
                  {t("app.appraisal.framework.helpText", { defaultValue: "Help text" })}
                  <textarea
                    rows={2}
                    maxLength={APPRAISAL_LIMITS.behaviorText}
                    placeholder={t("app.appraisal.framework.helpTextPlaceholder", {
                      defaultValue: "e.g. Look for reviews, CI green, and readable diffs",
                    })}
                    value={behaviorForm.helpText}
                    onChange={(e) =>
                      setBehaviorForm((p) => ({
                        ...p,
                        helpText: clampLen(e.target.value, APPRAISAL_LIMITS.behaviorText),
                      }))
                    }
                  />
                  <AppraisalCharCount value={behaviorForm.helpText} max={APPRAISAL_LIMITS.behaviorText} />
                </label>
                <div className="appraisal-framework-form__actions">
                  <button type="submit" className="appraisal-btn primary" disabled={!!saving}>
                    {saving === "behavior"
                      ? t("app.common.saving", { defaultValue: "Saving…" })
                      : t("app.appraisal.framework.addBehavior", { defaultValue: "Add behavior" })}
                  </button>
                </div>
              </form>

              <div className="appraisal-framework-list-block">
                <div className="appraisal-framework-list-block__head">
                  <span>
                    {t("app.appraisal.framework.behaviorsList", {
                      defaultValue: "Added behaviors",
                    })}
                  </span>
                  <span className="appraisal-framework-count">{behaviors.length}</span>
                </div>
                {behaviors.length === 0 ? (
                  <div className="appraisal-framework-empty">
                    <i className="bi bi-list-check" aria-hidden />
                    <p>
                      {t("app.appraisal.framework.behaviorsEmpty", {
                        defaultValue: "No behaviors yet. Link a Value and Group, then add a statement.",
                      })}
                    </p>
                  </div>
                ) : (
                  <ul className="appraisal-framework-items">
                    {behaviors.map((b) => (
                      <li key={b.id} className="appraisal-framework-item">
                        <div className="appraisal-framework-item__body">
                          <div className="appraisal-framework-item__meta">
                            <span>{b.appraisalValueName}</span>
                            <span aria-hidden>·</span>
                            <span>{b.appraisalBehaviorGroupName}</span>
                          </div>
                          <strong>{b.statement}</strong>
                          {b.helpText ? <span>{b.helpText}</span> : null}
                        </div>
                        <button
                          type="button"
                          className="appraisal-list-delete"
                          onClick={() => openDeleteConfirm("behavior", b)}
                          title={t("app.common.delete", { defaultValue: "Delete" })}
                        >
                          <i className="bi bi-trash3" aria-hidden />
                          <span>{t("app.common.delete", { defaultValue: "Delete" })}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}
        </div>
      )}

      <FrameworkHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} t={t} />

      {deleteConfirm ? (
        <div
          className="appraisal-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="appraisal-framework-delete-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteLoading) setDeleteConfirm(null);
          }}
        >
          <div className="appraisal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="appraisal-modal__header">
              <div>
                <p className="appraisal-modal__eyebrow">
                  {t("app.appraisal.frameworkTitle", { defaultValue: "Values & Behaviors" })}
                </p>
                <h2 className="appraisal-modal__title" id="appraisal-framework-delete-title">
                  {deleteConfirm.title}
                </h2>
              </div>
              <button
                type="button"
                className="appraisal-btn"
                aria-label={t("app.common.close", { defaultValue: "Close" })}
                disabled={deleteLoading}
                onClick={() => setDeleteConfirm(null)}
              >
                {t("app.common.close", { defaultValue: "Close" })}
              </button>
            </div>
            <div className="appraisal-modal__body">
              <div className="appraisal-modal__summary">
                <div className="appraisal-modal__icon" aria-hidden>
                  <i className="bi bi-trash3" />
                </div>
                <p className="appraisal-modal__lead">{deleteConfirm.lead}</p>
              </div>
              <div className="appraisal-modal__target">
                <p className="appraisal-modal__target-label">
                  {t("app.appraisal.framework.deleteTarget", { defaultValue: "Item" })}
                </p>
                <p className="appraisal-modal__target-name">{deleteConfirm.name}</p>
              </div>
              <div className="appraisal-modal__actions">
                <button
                  type="button"
                  className="appraisal-btn"
                  disabled={deleteLoading}
                  onClick={() => setDeleteConfirm(null)}
                >
                  {t("app.common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  className="appraisal-btn danger"
                  disabled={deleteLoading}
                  onClick={confirmDelete}
                >
                  {deleteLoading
                    ? t("app.common.deleting", { defaultValue: "Deleting…" })
                    : t("app.common.delete", { defaultValue: "Delete" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AppraisalFrameworkPage;
